import { Box, Chip, ComboboxSelect, Link, Skeleton, Stack, Typography } from '@chronosphereio/chrono-ui';
import { displayMetricType } from '@chronosphereio/editors/prometheus';
import { useDebouncedEffect } from '@react-hookz/web';
import { OpenInNew as OpenInNewIcon } from 'mdi-material-ui';
import { useState } from 'react';
import { Group } from '@/dashboards/components/Builder';
import { useVizFrameworkConfig } from '@/plugin-system';
import { DEFAULT_QUANTILE, isValidCalculation } from '../calculation-selection';
import { useLabelNames, useMetricMetadata, useMetricNames } from '../promql-builder-apis';
import { useBuilderContext } from '../promql-builder-context';
import {
  isClassicHistogramBucketMetric,
  isHistogramMetric,
  METRIC_TYPE_TO_DEFAULT_RANGE,
} from '../promql-builder-model';
import { getFuzzyMatchingMetrics } from './metric-selection-utils';

export function MetricSelection() {
  const {
    isLoading: isBuilderLoading,
    metric,
    hasMetric,
    metricType,
    isClassicHistogramMetric,
    calculation,
    onMetricChange,
    fetchMetricMetadata,
    setMetricMetadata,
    onBuilderChange,
  } = useBuilderContext();

  // Value
  const value = metric || null;

  // Options
  const { data, isLoading: isMetricNamesLoading, error } = useMetricNames();

  /**
   * Called when the user types in the search input.
   */
  const filterOptions = (options: string[], searchTerm: string) => {
    return getFuzzyMatchingMetrics(options, searchTerm);
  };

  /**
   * Called when a user selects a metric.
   * This function makes a request for the metric type, and uses that to update the builder.
   */
  const handleMetricSelect = async (metric: string) => {
    try {
      const metadata = await fetchMetricMetadata(metric);
      setMetricMetadata(metadata);

      const { metricType } = metadata;

      // Update builder depending on the metric type.
      if (metricType === undefined) {
        onBuilderChange({ metric, range: undefined }, metadata);
      } else {
        const newRange = METRIC_TYPE_TO_DEFAULT_RANGE[metricType];
        // Check that the current calculation can be applied to this metric type.
        const currentCalculation =
          calculation && isValidCalculation(calculation, metric, metadata) ? calculation : undefined;
        const newCalculation = isHistogramMetric(metric, metadata)
          ? (currentCalculation ?? { kind: 'percentile', quantile: DEFAULT_QUANTILE })
          : currentCalculation;

        onBuilderChange({ metric, range: newRange, calculation: newCalculation }, metadata);
      }
    } catch (err) {
      console.error(err, 'Err while fetching metric type');
      const metadata = { metricType: undefined, isClassicHistogramMetric: false };
      setMetricMetadata(metadata);
      onBuilderChange({ metric, range: undefined }, metadata);
    }
  };

  /**
   * Called when a user removes the metric.
   */
  const handleMetricRemove = () => {
    onMetricChange('');
  };

  const showRemove = hasMetric && !isBuilderLoading;
  const buttonContent = value ? (
    <>
      {value}
      {metricType && (
        <Box sx={{ ml: 1, color: (t) => t.palette.text.disabled }}>
          {isClassicHistogramBucketMetric(metric, isClassicHistogramMetric)
            ? 'Classic histogram'
            : displayMetricType(metricType)}
        </Box>
      )}
    </>
  ) : (
    'Select a metric'
  );

  return (
    <Group.Root onRemove={showRemove ? () => handleMetricRemove() : undefined}>
      <Group.Label>Metric</Group.Label>
      <ComboboxSelect
        onReset={() => null}
        secondaryContent={({ highlightedOptionValue }) => {
          if (highlightedOptionValue) {
            return <MetricDetailsContainer metric={highlightedOptionValue} allMetrics={data} />;
          } else if (value) {
            return <MetricDetailsContainer metric={value} allMetrics={data} />;
          }
        }}
        filterOptions={filterOptions}
        highlightSearchKeywords
        isLoading={isMetricNamesLoading}
        noOptionsText={error ? 'Unable to load metrics' : 'No metrics found'}
        searchPlaceholder="Search metrics, ex. http requests total"
        allowCustomValue
        value={value}
        renderValue={
          <Group.Button isLast={!showRemove} isPlaceholder={!hasMetric} aria-label="metric">
            {isBuilderLoading ? <Skeleton width="25px" /> : buttonContent}
          </Group.Button>
        }
        options={data}
        onSelect={async (option) => {
          if (!option) {
            handleMetricRemove();
          } else {
            await handleMetricSelect(option);
          }
        }}
      />
    </Group.Root>
  );
}

function MetricDetailsContainer({ metric, allMetrics }: { metric: string; allMetrics?: string[] }) {
  const [rawHighlightedMetric, setRawHighlightedMetric] = useState<string | null>(metric);

  useDebouncedEffect(() => setRawHighlightedMetric(metric), [metric], 500);

  if (!rawHighlightedMetric) return null;

  return (
    <Stack
      sx={{
        borderLeft: '1px solid',
        borderColor: 'border.default',
        width: 600,
      }}
    >
      <MetricDetails metric={rawHighlightedMetric} allMetrics={allMetrics} />
    </Stack>
  );
}

function MetricDetails({ metric, allMetrics }: { metric: string; allMetrics?: string[] }) {
  const config = useVizFrameworkConfig();

  const { data: metadata, isLoading: isMetadataLoading } = useMetricMetadata(metric);
  const { type, isClassicHistogram } = metadata ?? {};
  const { data: labelNames, isLoading: isLabelNamesLoading } = useLabelNames(metric);

  const isUnknownMetric = allMetrics && !allMetrics.includes(metric);

  return (
    <>
      <Stack
        spacing={1}
        padding={1}
        justifyContent="space-between"
        borderBottom={(t) => `1px solid ${t.palette.border.default}`}
        // `height` and `maxHeight` keep this the same height as the metric list header
        height="100%"
        maxHeight="77px"
      >
        <Typography variant="h4" overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis" title={metric}>
          {metric}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          {isMetadataLoading ? (
            <Skeleton width={100} height={20} />
          ) : (
            <Stack direction="row">
              {isUnknownMetric ? (
                <Typography variant="body2" sx={{ marginTop: '0px !important' }} color="text.legacySecondary">
                  Unknown metric
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ marginTop: '0px !important' }}>
                  {displayMetricType(type)}
                </Typography>
              )}
              {!isUnknownMetric && isClassicHistogram && (
                <Link
                  marginX={0.5}
                  display="flex"
                  alignItems="center"
                  variant="body2"
                  href={`https://docs.chronosphere.io/investigate/querying/promql/apply-functions#querying-classic-prometheus-histograms`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {`(`}classic histogram <OpenInNewIcon fontSize={'inherit'} sx={{ marginLeft: 0.25 }} />
                  {`)`}
                </Link>
              )}
            </Stack>
          )}
          {!isUnknownMetric && (
            <Link
              variant="body2"
              href={`/metrics/catalog?metric=${encodeURIComponent(metric)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              View in Metrics Catalog
            </Link>
          )}
        </Stack>
      </Stack>
      {isUnknownMetric ? (
        <Stack spacing={1} padding={1}>
          <Typography variant="body2">This metric was not found.</Typography>
        </Stack>
      ) : (
        <>
          <Stack spacing={2} padding={1} overflow="auto" height="100%">
            {config?.QueryBuilderMetricDescription && <config.QueryBuilderMetricDescription metricName={metric} />}
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h4" color="text.legacySecondary" fontSize="body2.fontSize">
                  Labels
                </Typography>
                <Link
                  variant="body2"
                  href={`/metrics/catalog?metric=${encodeURIComponent(metric)}&tab=labels`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View all values
                </Link>
              </Stack>
              {isLabelNamesLoading ? (
                <Skeleton width={100} height={20} />
              ) : (
                <>
                  {labelNames && labelNames.length > 0 && (
                    <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                      {labelNames.map((label) => (
                        <Chip key={label} label={label} />
                      ))}
                    </Stack>
                  )}
                </>
              )}
            </Stack>
          </Stack>

          {config?.QueryBuilderComments && <config.QueryBuilderComments entityId={metric} entityType={'METRIC_NAME'} />}
        </>
      )}
    </>
  );
}
